import { Component, OnInit, Inject, signal, ViewChildren, QueryList } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { NgTemplateOutlet } from '@angular/common';
import { ScrollingModule, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { User, Ride, AutomaticallyPlannedRide } from '../../../interfaces';
import { db, dataStore } from '../../../app.component';
import { valueToDate } from '../../../globals';
import moment from 'moment';

export interface DialogData {
  user: User;
}

interface UniformRideData {
  id: string;
  start: Date;
  pickupTime: Date;
  dropoffTime: Date;
  originalUserPickUpTime?: Date | null;
  originalUserDropOffTime?: Date | null;
  automaticallyPlanned: boolean;
  from: any;
  to: any;
  vehicleName?: string;
  isPlanned: boolean;
  cardColor?: string;
}

@Component({
  selector: 'app-user-future-rides',
  templateUrl: './user-future-rides.component.html',
  styleUrls: ['./user-future-rides.component.scss'],
  standalone: true,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButton,
    MatIcon,
    MatProgressSpinner,
    MatTabGroup,
    MatTab,
    NgTemplateOutlet,
    ScrollingModule,
  ],
})
export class UserFutureRidesComponent implements OnInit {
  @ViewChildren(CdkVirtualScrollViewport) viewports!: QueryList<CdkVirtualScrollViewport>;

  templateOutlet = NgTemplateOutlet;
  loading = signal(true);
  organisationId: string = localStorage.getItem('orgId') || dataStore.selectedOrganisation.id;
  usesAutomaticPlanning: boolean = dataStore.paidFunctionalities?.usesAutomaticPlanning ?? false;

  plannedRides = signal<UniformRideData[]>([]);
  automaticRides = signal<UniformRideData[]>([]);

  selectedTabIndex = signal(0);
  savedScrollOffsets = [0, 0];
  readonly rideItemSize = 240;

  constructor(@Inject(MAT_DIALOG_DATA) public data: DialogData) {}

  async ngOnInit() {
    try {
      const todayStart = moment().startOf('day').toDate();

      // Fetch active vehicles to map vehicleId to vehicle name
      const vehiclesQuery = query(
        collection(db, `organisations/${this.organisationId}/vehicles`),
        where('active', '==', true),
      );
      const vehiclesSnapshot = await getDocs(vehiclesQuery);
      const vehicleNameMap = new Map<string, string>();
      const vehicleColorMap = new Map<string, string>();
      vehiclesSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        vehicleNameMap.set(docSnap.id, data['name'] || '');
        const color = data['calendarStyle']?.['color'];
        if (color) {
          vehicleColorMap.set(docSnap.id, color);
        }
      });

      // Query planned rides where this user is in userIds
      const plannedRidesQuery = query(
        collection(db, `organisations/${this.organisationId}/rides`),
        where('userIds', 'array-contains', this.data.user.id),
        where('start', '>=', todayStart),
      );
      const plannedRidesSnapshot = await getDocs(plannedRidesQuery);

      const plannedRidesList: UniformRideData[] = [];
      plannedRidesSnapshot.forEach((docSnap) => {
        const ride = docSnap.data() as Ride;
        ride.id = docSnap.id;

        const userInRide = ride.users?.[this.data.user.id];
        const {
          pickupTime,
          dropoffTime,
          from,
          to,
          originalUserPickUpTime,
          originalUserDropOffTime,
        } = this.extractRideInfo(ride, userInRide);
        const vehicleName = ride.vehicleId ? vehicleNameMap.get(ride.vehicleId) || '' : '';
        const cardColor = ride.vehicleId ? vehicleColorMap.get(ride.vehicleId) : undefined;

        plannedRidesList.push({
          id: ride.id,
          start: valueToDate(ride.start),
          pickupTime,
          dropoffTime,
          originalUserPickUpTime,
          originalUserDropOffTime,
          automaticallyPlanned: ride.automaticallyPlanned === true,
          from,
          to,
          vehicleName,
          isPlanned: true,
          cardColor,
        });
      });

      const now = new Date();
      this.plannedRides.set(this.filterAndSortRides(plannedRidesList, now));

      // If automatic planning is enabled, query the automaticRides collection as well
      if (this.usesAutomaticPlanning) {
        const automaticRidesQuery = query(
          collection(db, `organisations/${this.organisationId}/automaticRides`),
          where('passenger.id', '==', this.data.user.id),
          where('start', '>=', todayStart),
        );
        const automaticRidesSnapshot = await getDocs(automaticRidesQuery);

        const automaticRidesList: UniformRideData[] = [];
        automaticRidesSnapshot.forEach((docSnap) => {
          const ride = docSnap.data() as AutomaticallyPlannedRide;
          ride.id = docSnap.id;

          const passenger = ride.passenger;
          const { pickupTime, dropoffTime, from, to } = this.extractRideInfo(
            ride,
            passenger,
            false,
          );
          const cardColor = ride.useBuffers ? '#6c5ce7' : '#ff9800';

          if (!ride.planningCompleted || ride.rejected) {
            automaticRidesList.push({
              id: ride.id,
              start: valueToDate(ride.start),
              pickupTime,
              dropoffTime,
              from,
              to,
              isPlanned: false,
              automaticallyPlanned: false,
              cardColor,
            });
          }
        });

        this.automaticRides.set(this.filterAndSortRides(automaticRidesList, now));
      }
    } catch (error) {
      console.error('Error loading user future rides:', error);
    } finally {
      this.loading.set(false);
    }
  }

  extractRideInfo(
    ride: any,
    passengerNode: any,
    isPlanned = true,
  ): {
    pickupTime: Date;
    dropoffTime: Date;
    originalUserPickUpTime: Date | null;
    originalUserDropOffTime: Date | null;
    from: any;
    to: any;
  } {
    return {
      originalUserPickUpTime: isPlanned ? valueToDate(passengerNode?.originalUserPickUpTime) : null,
      originalUserDropOffTime: isPlanned
        ? valueToDate(passengerNode?.originalUserDropOffTime)
        : null,
      pickupTime: valueToDate(
        isPlanned ? (passengerNode?.userPickupTime ?? ride.start) : ride.start,
      ),
      dropoffTime: valueToDate(isPlanned ? (passengerNode?.userDropoffTime ?? ride.end) : ride.end),
      from: passengerNode?.from,
      to: passengerNode?.to,
    };
  }

  filterAndSortRides(rides: UniformRideData[], now: Date): UniformRideData[] {
    return rides
      .filter((r) => {
        const isToday = moment(r.start).isSame(now, 'day');
        if (isToday) {
          return r.dropoffTime && r.dropoffTime.getTime() >= now.getTime();
        }
        return true;
      })
      .sort((a, b) => a.pickupTime.getTime() - b.pickupTime.getTime());
  }

  private getTabId(index: number): string {
    return index === 0 ? 'automatic' : 'planned';
  }

  onTabChange(newIndex: number) {
    const oldIndex = this.selectedTabIndex();
    const oldId = this.getTabId(oldIndex);

    const oldViewport = this.viewports.find(
      (v) => v.elementRef.nativeElement.getAttribute('id') === oldId,
    );
    if (oldViewport) {
      this.savedScrollOffsets[oldIndex] = oldViewport.measureScrollOffset();
    }

    // Switch the active tab index
    this.selectedTabIndex.set(newIndex);
  }

  onAnimationDone() {
    const newIndex = this.selectedTabIndex();
    const newId = this.getTabId(newIndex);

    const targetViewport = this.viewports.find(
      (v) => v.elementRef.nativeElement.getAttribute('id') === newId,
    );
    if (targetViewport) {
      targetViewport.checkViewportSize();
      targetViewport.scrollToOffset(this.savedScrollOffsets[newIndex]);
    }
  }

  // Required because *cdkVirtualFor (like *ngFor) is a structural directive and does not support Angular 17+'s inline template tracking syntax (e.g., track ride.id).
  // It strictly requires a backing component method reference to perform DOM node tracking and recycling.
  trackById(index: number, item: UniformRideData): string {
    return item.id;
  }

  onMouseEnter(event: MouseEvent, fullText: string) {
    const el = event.target as HTMLElement;
    if (el) {
      el.title = el.scrollWidth > el.clientWidth ? fullText : '';
    }
  }

  getAddressString(loc: any): string {
    if (!loc) return '';
    if (typeof loc === 'string') return loc;
    if (typeof loc === 'object') {
      if (loc.address) return loc.address;
      if (loc.name) return loc.name;
    }
    return '';
  }

  formatDate(date: Date): string {
    if (!date) return '';
    return moment(date).format('DD-MM-YYYY');
  }

  formatTime(date: Date): string {
    if (!date) return '';
    return moment(date).format('HH:mm');
  }
}
